import React, { FC, useMemo } from "react";
import { Field } from "react-final-form";
import { makeStyles, Typography } from "@material-ui/core";
import moment, { Moment } from "moment";
import clsx from "clsx";
import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import DatePicker from "../../../../../components/DatePicker";
import { DATE_FORMATS } from "../../../../../constants";
import { calculateIntakeName } from "../../../../../helpers";
import { ApplicationStatus } from "../../../../application/types";
import LabeledDot from "../../../../../components/LabeledDot/LabeledDot";
import { WizardMode } from "../../../../wizard/types";

const useStyles = makeStyles(() => ({
  title: {
    fontSize: 14,
    lineHeight: 1,
    color: "#a1a4ac",
  },
  subContainer: {
    display: "flex",
    columnGap: 16,
    marginBottom: 24,
  },
  deadlineContainer: {
    "& > div": {
      width: 238,
    },
  },
  deadlineContainerEditMode: {
    "& > div": {
      width: 178,
    },
  },
  startDateContainer: {
    "& > div": {
      width: 238,
    },
  },
  startDateContainerEditMode: {
    "& > div": {
      width: 178,
    },
  },
  intakeNameContainer: {
    width: 60,
  },
  appMainContainer: {
    width: 110,
  },
  appContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: 16,
    paddingTop: 14,
  },
  actionsMainContainer: {
    width: 80,
  },
  actionsContainer: {
    paddingTop: 8,
    display: "flex",
    columnGap: 16,
  },
  intakeName: {
    fontSize: 16,
    paddingTop: 12,
    color: "#bcbcbc",
  },
}));

interface IIntakeItemProps {
  name: string;
  mode: WizardMode;
  deadline: Moment;
  startDate: Moment;
  onDelete: any;
  recoverIntakeHandler?: any;
  isDeleteDisabled?: boolean;
  isStartDateDisabled?: boolean;
  isIntakeDeleted?: boolean;
  approvedAppCount?: string;
  pendingAppCount?: string;
  state: boolean;
  onStateChange?: any;
}

export const IntakeItem: FC<IIntakeItemProps> = ({
  name,
  mode,
  deadline,
  startDate,
  onDelete,
  recoverIntakeHandler,
  isDeleteDisabled = false,
  isStartDateDisabled = false,
  isIntakeDeleted = false,
  approvedAppCount = "N/A",
  pendingAppCount = "N/A",
  state = true,
  onStateChange,
}) => {
  const classes = useStyles();

  const isEditMode = mode === WizardMode.Edit;
  const maxDeadline = useMemo(
    () => (startDate ? moment(startDate).subtract(3, "day") : undefined),
    [startDate],
  );
  const minStartDate = useMemo(
    () => (deadline ? moment(deadline).add(3, "day") : undefined),
    [deadline],
  );

  return (
    <div className={classes.subContainer}>
      <div
        className={clsx(
          classes.deadlineContainer,
          isEditMode && classes.deadlineContainerEditMode,
        )}
      >
        <Field
          maxDate={maxDeadline}
          infoIcon="Indicate the application deadline"
          disableToolbar
          variant="inline"
          autoOk
          fullWidth
          name={`${name}.deadline`}
          label="Deadline"
          dateFormat={DATE_FORMATS.frontEnd}
          component={DatePicker}
          placeholder="Please Select"
          disabled={isIntakeDeleted}
        />
      </div>
      <div
        className={clsx(
          classes.startDateContainer,
          isEditMode && classes.startDateContainerEditMode,
        )}
      >
        <Field
          minDate={minStartDate}
          infoIcon="When classes start for each intake"
          disableToolbar
          variant="inline"
          autoOk
          fullWidth
          name={`${name}.startDate`}
          label="Start Date"
          dateFormat={DATE_FORMATS.frontEnd}
          component={DatePicker}
          placeholder="Please Select"
          disabled={isIntakeDeleted || isStartDateDisabled}
        />
      </div>
      <div className={classes.intakeNameContainer}>
        <div className={classes.title}>Intake</div>
        <Typography className={classes.intakeName}>
          {calculateIntakeName(startDate)}
        </Typography>
      </div>
      <div className={classes.actionsMainContainer}>
        <div className={classes.title}>Actions</div>
        <div className={classes.actionsContainer}>
          <ActionBar
            switchCallBack={onStateChange}
            switchTooltipActive="Deactivate intake"
            switchTooltipNotActive="Activate intake"
            switchState={state}
            isSwitchDisabled={isIntakeDeleted}
            deleteCallBack={!isIntakeDeleted && onDelete}
            deleteTooltip="Delete intake"
            isDeleteDisabled={isDeleteDisabled}
            recoverCallBack={isIntakeDeleted && recoverIntakeHandler}
            recoverTooltip="Recover intake"
          />
        </div>
      </div>
      {isEditMode && (
        <div className={classes.appMainContainer}>
          <div className={classes.title}>Applications</div>
          <div className={classes.appContainer}>
            <LabeledDot
              color={ApplicationStatus.APPROVED}
              label={approvedAppCount}
              tooltip="Approved applications"
            />
            <LabeledDot
              color={ApplicationStatus.PENDING}
              label={pendingAppCount}
              tooltip="Pending applications"
            />
          </div>
        </div>
      )}
    </div>
  );
};
